import { ActivityLevel } from "@/clients/models/ActivityLevel";
import { OnboardingOptionPill } from "@/features/onboarding/components/onboarding-option-pill";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { useGetActivityLevel } from "@/hooks/requests/acivity/use-get-activity-level";
import { useUpdateActivityLevel } from "@/hooks/requests/acivity/use-update-activity-level";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { triggerHaptic } from "@/utils/haptics";
import { useState } from "react";

/**
 * The v5 wording maps onto the backend's 5-value activity enum. The "Extra active"
 * tier has no v5 answer and is currently unreachable anywhere in the app — there is
 * no Settings screen for activity level. The enum value stays on the backend because
 * existing users are stored on it; nobody onboarding now can select it.
 */
const ACTIVITY_OPTIONS = [
  { label: "Mostly sitting", value: ActivityLevel.activityLevel.SEDENTARY_NO_EXERCISE },
  {
    label: "Somewhat active",
    value: ActivityLevel.activityLevel.LIGHTLY_ACTIVE_EXERCISE_1_3_DAYS_WEEK,
  },
  {
    label: "Very active",
    value: ActivityLevel.activityLevel.MODERATELY_ACTIVE_EXERCISE_3_5_DAYS_WEEK,
  },
  {
    label: "Highly active",
    value: ActivityLevel.activityLevel.VERY_ACTIVE_EXERCISE_6_7_DAYS_A_WEEK,
  },
];

const ActivityPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { activityLevel } = useGetActivityLevel();
  const { updateActivityLevelAsync, isUpdating } = useUpdateActivityLevel();
  const { showError } = useToast();
  const [selectedValue, setSelectedValue] = useState<ActivityLevel.activityLevel | null>(null);

  const selected = selectedValue ?? activityLevel?.activityLevel ?? null;

  const handleContinue = async () => {
    if (!selected || isUpdating) return;
    triggerHaptic();
    try {
      await Promise.all([
        preloadNextRoute(),
        updateActivityLevelAsync({ activityLevel: selected }),
      ]);
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <OnboardingStepShellV5
      title="What does most of your day look like?"
      subtitle="This helps me estimate your activity level."
      footer={
        <OnboardingPillButton
          text="Continue"
          onClick={handleContinue}
          disabled={!selected || isUpdating}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {ACTIVITY_OPTIONS.map((option) => (
          <OnboardingOptionPill
            key={option.value}
            label={option.label}
            selected={selected === option.value}
            onClick={() => setSelectedValue(option.value)}
          />
        ))}
      </div>
    </OnboardingStepShellV5>
  );
};

export default ActivityPage;
